import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Quote, HeartHandshake, PhoneCall } from 'lucide-react';
import { Reveal, Words } from '../motion/Reveal';

const HERO_IMG = 'https://images.unsplash.com/photo-1756142007128-f431ede241cc?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400';
const TEAM_IMG = 'https://images.unsplash.com/photo-1652509573480-a190f1c61f6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';
const H2 = 'text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight';

export const AboutHero = ({ c }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-[#eef6f0] via-white to-[#e6f4ea] pt-10 md:pt-16 pb-16 md:pb-24" data-testid="about-hero">
    <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-[radial-gradient(#1a5c3b22_1px,transparent_1px)] [background-size:22px_22px]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Reveal as="span" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#1a5c3b] bg-white border border-emerald-200 shadow-sm px-4 py-1.5 rounded-full mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{c.eyebrow}
        </Reveal>
        <Words as="h1" parts={[[c.h1a], [c.h1b, 'text-transparent bg-clip-text bg-gradient-to-r from-[#1a5c3b] to-emerald-500']]} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6" data-testid="about-heading" />
        <Reveal delay={350} className="w-16 h-1 rounded-full bg-[#1a5c3b] mb-6" />
        <Reveal as="p" delay={450} className="text-xl md:text-2xl font-semibold text-[#0f172a] leading-snug mb-4">{c.lead}</Reveal>
        <Reveal as="p" delay={550} className="text-lg text-slate-500 leading-relaxed mb-6">{c.intro}</Reveal>
        <Reveal delay={650} className="border-l-4 border-[#1a5c3b] bg-white/80 rounded-r-2xl px-6 py-4 shadow-sm" data-testid="about-mission">
          <p className="text-lg text-slate-700"><strong className="text-[#1a5c3b]">{c.mission[0]}</strong>{c.mission[1]}</p>
        </Reveal>
      </div>
      <Reveal delay={250} className="lg:col-span-5 relative">
        <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-[#1a5c3b] to-emerald-400 rotate-3 opacity-90" />
        <img src={HERO_IMG} alt="" className="relative w-full h-[360px] md:h-[460px] object-cover rounded-[2rem] shadow-2xl" />
        <div className="absolute -bottom-6 -left-4 md:-left-10 bg-white rounded-2xl shadow-[0_20px_50px_rgba(15,23,42,0.18)] px-6 py-4 flex items-center gap-4" data-testid="about-years-badge">
          <span className="text-5xl font-extrabold text-[#1a5c3b] leading-none">{c.years}</span>
          <span className="text-sm font-semibold text-slate-600 max-w-[150px] leading-tight">{c.yearsLabel}</span>
        </div>
      </Reveal>
    </div>
  </section>
);

export const AboutTeam = ({ c }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-testid="about-team">
    <Reveal className="relative order-2 lg:order-1">
      <img src={TEAM_IMG} alt="" loading="lazy" className="w-full h-80 md:h-[480px] object-cover rounded-[2rem] shadow-xl" />
      <div className="absolute -bottom-6 right-6 left-6 md:left-auto md:max-w-sm bg-[#1a5c3b] text-white rounded-2xl p-6 shadow-2xl">
        <Quote className="w-7 h-7 text-emerald-300 mb-2" />
        <p className="text-lg font-semibold leading-snug">{c.teamQuote}</p>
      </div>
    </Reveal>
    <div className="order-1 lg:order-2">
      <Words parts={[[c.teamTitle]]} className={`${H2} mb-8`} />
      <div className="space-y-5">
        {c.team.map((t, i) => <Reveal as="p" key={t} delay={150 + i * 120} className={`text-lg leading-relaxed ${i === 2 ? 'text-[#0f172a] font-semibold' : 'text-slate-600'}`}>{t}</Reveal>)}
      </div>
    </div>
  </section>
);

export const AboutDiff = ({ c }) => (
  <section className="bg-gradient-to-b from-[#eef6f0] to-[#f8faf7] py-16 md:py-24" data-testid="about-diff">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Words parts={[[c.diffTitle]]} className={`${H2} mb-12 text-center max-w-4xl mx-auto`} />
      <div className="grid md:grid-cols-2 gap-6">
        {c.diff.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={(i % 2) * 150} className="group relative bg-white rounded-3xl p-8 border border-emerald-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 overflow-hidden" data-testid={`about-diff-${i + 1}`}>
            <span className="absolute -top-4 right-4 text-[7rem] font-black leading-none text-transparent [-webkit-text-stroke:1.5px_rgba(26,92,59,0.15)] select-none">{String(i + 1).padStart(2, '0')}</span>
            <div className="relative w-14 h-14 rounded-2xl bg-[#1a5c3b] text-white flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-[-6deg] transition-transform duration-300"><Icon className="w-7 h-7" /></div>
            <h3 className="relative text-2xl font-bold mb-3">{t}</h3>
            <p className="relative text-slate-600 leading-relaxed text-lg">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const AboutCommit = ({ c }) => (
  <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="about-commit">
    <Words parts={[[c.commitTitle]]} className={`${H2} mb-8`} />
    <div className="grid md:grid-cols-3 gap-8 items-start">
      <div className="md:col-span-2 space-y-5">
        {c.commit.map((t, i) => <Reveal as="p" key={t} delay={150 + i * 120} className={`text-lg leading-relaxed ${i === 0 ? 'text-xl font-semibold text-[#0f172a]' : 'text-slate-600'}`}>{t}</Reveal>)}
      </div>
      <div className="space-y-3">
        {c.commitWords.map((w, i) => (
          <Reveal key={w} delay={300 + i * 150} className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4">
            <CheckCircle2 className="w-6 h-6 text-[#1a5c3b]" /><span className="font-bold text-lg text-[#0f172a]">{w}</span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const AboutMotto = ({ c }) => (
  <section className="px-4 pb-16 md:pb-24" data-testid="about-motto">
    <div className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] px-8 py-14 md:px-16 md:py-20 text-center">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px]" />
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-emerald-400/30 blur-3xl" />
      <div className="relative">
        <HeartHandshake className="w-14 h-14 text-emerald-300 mx-auto mb-6" />
        <Words parts={[[c.motto]]} className="text-4xl md:text-5xl lg:text-6xl font-bold !text-white leading-tight mb-8" data-testid="about-motto-title" />
        {c.mottoText.map((t, i) => <Reveal as="p" key={t} delay={200 + i * 150} className="text-emerald-50/90 text-lg md:text-xl max-w-3xl mx-auto mb-4">{t}</Reveal>)}
        <Reveal as="p" delay={550} className="text-xl md:text-2xl font-bold text-emerald-300 mt-8">{c.mottoEnd}</Reveal>
      </div>
    </div>
  </section>
);

export const AboutCta = ({ c }) => (
  <section className="max-w-4xl mx-auto px-4 pb-20 text-center" data-testid="about-cta">
    <Words parts={[[c.ctaTitle]]} className={`${H2} mb-5`} />
    <Reveal as="p" delay={150} className="text-lg md:text-xl text-slate-600 mb-8">{c.ctaText}</Reveal>
    <Reveal delay={300} className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link to="/demander-un-pret-auto" className="group inline-flex items-center justify-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold text-base md:text-lg px-8 py-4 rounded-full shadow-[0_12px_35px_rgba(26,92,59,0.35)] hover:scale-[1.03] transition-[transform,background-color]" data-testid="about-cta-btn">
        {c.ctaBtn}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Link>
      <a href="tel:5144183968" className="inline-flex items-center justify-center gap-2 border-2 border-[#1a5c3b] text-[#1a5c3b] font-semibold px-8 py-4 rounded-full hover:bg-[#1a5c3b] hover:text-white transition-colors" data-testid="about-cta-call">
        <PhoneCall className="w-5 h-5" />514-418-3968
      </a>
    </Reveal>
    <Reveal delay={450} className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm font-semibold text-[#1a5c3b]" data-testid="about-cta-perks">
      {c.ctaPerks.map((p) => <span key={p} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{p}</span>)}
    </Reveal>
  </section>
);
